import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from "../context/ProjectContext";
import { useOrganization } from "../context/OrganizationContext";
import { AuthContext } from "../context/AuthProvider";
import { getFormsByProjectId, getSubmissionsByFormId } from "../services/apiService";
import {
    Settings2,
    FolderKanban,
    FileText,
    Inbox,
    Zap,
    Bell,
    UserCircle2,
    CreditCard,
    LogOut,
    Pencil,
    LucideIcon,
} from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { staggerDelay } from "../utils/animation";

const Dashboard = () => {
    const navigate = useNavigate();
    const [showSettings, setShowSettings] = useState(false);
    const { selectedOrganization } = useOrganization();
    const { logout } = useContext(AuthContext);
    const { setSelectedProjectId, projects, setProjects } = useProject();
    const [forms, setForms] = useState<any[]>([]);
    const [submissions, setSubmissions] = useState<any[]>([]);

    useEffect(() => {
        if (!selectedOrganization || !projects || projects.length === 0) return;

        const fetchData = async () => {
            try {
                // Assicurati di aggiornare i progetti nel context
                setProjects(projects);

                const allFormsResults = await Promise.all(
                    projects.map(async (project: any) => {
                        const projectForms = await getFormsByProjectId(project._id);
                        return (projectForms || []).map((f: any) => ({
                            ...f,
                            projectId: project._id,
                        }));
                    })
                );

                const flatForms = allFormsResults.flat();
                setForms(flatForms);

                const allSubmissionsResults = await Promise.all(
                    flatForms.map(async (form: any) => {
                        try {
                            const subs = await getSubmissionsByFormId(form._id);
                            return Array.isArray(subs)
                                ? subs.map((s: any) => ({ ...s, formId: form._id }))
                                : [];
                        } catch {
                            return [];
                        }
                    })
                );

                const flatSubmissions = allSubmissionsResults.flat();
                setSubmissions(flatSubmissions);
            } catch (err) {
                console.error("Error fetching dashboard data:", err);
            }
        };

        fetchData();
    }, [selectedOrganization, projects, setProjects]);

    const totalForms = forms.length;
    const totalSubmissions = submissions.length;
    const today = new Date().toDateString();

    const activeToday = submissions.filter(
        (s: any) =>
            s?.metadata?.created_at &&
            new Date(s.metadata.created_at).toDateString() === today
    ).length;

    const metrics = {
        totalProjects: projects?.length || 0,
        totalForms,
        totalSubmissions,
        activeToday,
    };

    const notifications = [
        `You received ${totalSubmissions} total submissions.`,
        `${activeToday} forms active today.`,
        `You have access to ${projects?.length || 0} projects.`,
    ];

    const recentForms = forms.map(f => ({
        id: f._id,
        name: f.name,
        date: f.metadata?.created_at
            ? new Date(f.metadata.created_at).toLocaleDateString()
            : 'N/A',
        projectId: f.project_id,
    }));

    const handleLogout = () => logout();

    const statCards: { label: string; value: number; icon: LucideIcon; tone: "primary" | "info" | "success" | "warning" }[] = [
        { label: "Projects", value: metrics.totalProjects, icon: FolderKanban, tone: "primary" },
        { label: "Forms Created", value: metrics.totalForms, icon: FileText, tone: "info" },
        { label: "Total Submissions", value: metrics.totalSubmissions, icon: Inbox, tone: "success" },
        { label: "Active Today", value: metrics.activeToday, icon: Zap, tone: "warning" },
    ];

    const toneClasses: Record<string, string> = {
        primary: "bg-primary-tint text-primary",
        info: "bg-info-tint text-info",
        success: "bg-success-tint text-success",
        warning: "bg-warning-tint text-warning",
    };

    return (
        <div className="bg-transparent h-full overflow-y-auto pr-1">
            <header className="flex justify-between items-start mb-8">
                <div>
                    <h1 className="text-2xl font-display font-bold text-ink">Dashboard</h1>
                    <p className="text-sm text-ink-muted mt-1">
                        Welcome back! Here's what's happening with your forms.
                    </p>
                </div>
                <Button
                    variant="secondary"
                    size="sm"
                    icon={Settings2}
                    onClick={() => setShowSettings(!showSettings)}
                >
                    {showSettings ? 'Hide Settings' : 'Show Settings'}
                </Button>
            </header>

            {/* Quick Settings */}
            {showSettings && (
                <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 animate-fade-in">
                    <Card>
                        <div className="flex items-center gap-2 mb-3">
                            <Bell size={15} className="text-ink-muted"/>
                            <h3 className="text-sm font-semibold text-ink">Notifications</h3>
                        </div>
                        <ul className="text-sm space-y-1.5">
                            {notifications.map((note, index) => (
                                <li key={index} className="text-ink-muted">• {note}</li>
                            ))}
                        </ul>
                    </Card>
                    <Card>
                        <div className="flex items-center gap-2 mb-3">
                            <UserCircle2 size={15} className="text-ink-muted"/>
                            <h3 className="text-sm font-semibold text-ink">Account</h3>
                        </div>
                        <button className="flex items-center gap-2 w-full text-left text-sm text-ink-muted hover:text-primary transition-colors py-1">
                            <UserCircle2 size={15}/> Manage Profile
                        </button>
                        <button className="flex items-center gap-2 w-full text-left text-sm text-ink-muted hover:text-primary transition-colors py-1">
                            <CreditCard size={15}/> Billing
                        </button>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 w-full text-left text-sm text-error hover:underline mt-2 py-1"
                        >
                            <LogOut size={15}/> Logout
                        </button>
                    </Card>
                </section>
            )}

            {/* Metrics */}
            <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mb-8">
                {statCards.map((stat, i) => (
                    <Card key={stat.label} className="animate-fade-in-up" style={staggerDelay(i)}>
                        <div className={`size-9 rounded-xl flex items-center justify-center mb-4 ${toneClasses[stat.tone]}`}>
                            <stat.icon size={18} strokeWidth={2}/>
                        </div>
                        <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wide">{stat.label}</h3>
                        <p className="text-3xl font-display font-bold text-ink mt-1.5">{stat.value}</p>
                    </Card>
                ))}
            </section>

            {/* Recent Forms */}
            <section className="animate-fade-in-up" style={staggerDelay(statCards.length)}>
                <h2 className="text-base font-display font-bold text-ink mb-4">Recent Forms</h2>
                <Card padding="none">
                    {recentForms.length === 0 ? (
                        <EmptyState
                            icon={FileText}
                            title="No forms yet"
                            description="Forms you create will show up here once submissions start coming in."
                        />
                    ) : (
                        <div className="divide-y divide-border">
                            {recentForms.map((form, i) => (
                                <div
                                    key={i}
                                    className="p-4 flex items-center justify-between gap-4 hover:bg-surface-hover transition-colors first:rounded-t-2xl last:rounded-b-2xl animate-fade-in-up"
                                    style={staggerDelay(i)}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="size-9 shrink-0 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
                                            <FileText size={16}/>
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-semibold text-ink truncate">{form.name}</h3>
                                            <p className="text-xs text-ink-muted mt-0.5">Last updated: {form.date}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        icon={Pencil}
                                        onClick={() => {
                                            setSelectedProjectId(form.projectId);
                                            navigate('/forms');
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </section>
        </div>
    );
};

export default Dashboard;
